import { get, post, postNoContent, setToken } from '@/shared/services/http';
import {
  forgotPasswordResultSchema,
  loginOutcomeSchema,
  profileSchema,
  twoFactorSentSchema,
  verifyResetCodeResultSchema,
  type Credentials,
  type LoginOutcome,
  type Profile,
  type TwoFactorChannel,
  type TwoFactorSent,
} from '../models/auth.model';

const DEVICE_STORAGE_KEY = 'msc.device';

/**
 * Identificador estável deste navegador.
 *
 * É o que permite reconhecer o dispositivo e não pedir o código de verificação a
 * cada login. Fica em `localStorage` porque precisa sobreviver ao fechamento da
 * aba; limpar os dados do navegador tem o efeito esperado — pedir o código de novo.
 */
export function getDeviceUuid(): string {
  try {
    const stored = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (stored) return stored;

    const generated = crypto.randomUUID();
    localStorage.setItem(DEVICE_STORAGE_KEY, generated);
    return generated;
  } catch {
    // Sem armazenamento, cada login trata o navegador como novo.
    return crypto.randomUUID();
  }
}

export function login(credentials: Credentials): Promise<LoginOutcome> {
  return post(loginOutcomeSchema, '/auth/login', {
    ...credentials,
    deviceUuid: getDeviceUuid(),
    origem: 'web',
  });
}

export function requestTwoFactorCode(
  challengeToken: string,
  channel: TwoFactorChannel,
): Promise<TwoFactorSent> {
  return post(twoFactorSentSchema, '/auth/two-factor/request', { challengeToken, channel });
}

export function verifyTwoFactorCode(params: {
  challengeToken: string;
  code: string;
  rememberDevice: boolean;
}): Promise<LoginOutcome> {
  return post(loginOutcomeSchema, '/auth/two-factor/verify', params);
}

export function me(): Promise<Profile> {
  return get(profileSchema, '/auth/me');
}

export function forgotPassword(email: string): Promise<{ message: string; token: string }> {
  return post(forgotPasswordResultSchema, '/auth/forgot-password', { email });
}

export function verifyResetCode(token: string, code: string): Promise<{ resetToken: string }> {
  return post(verifyResetCodeResultSchema, '/auth/forgot-password/verify-code', { token, code });
}

export function resetPassword(
  resetToken: string,
  password: string,
  passwordConfirmation: string,
): Promise<void> {
  return postNoContent('/auth/reset-password', {
    token: resetToken,
    password,
    passwordConfirmation,
  });
}

/**
 * Encerra a sessão.
 *
 * O token local é descartado mesmo quando a chamada falha: manter uma sessão que
 * o usuário pediu para encerrar é pior que perder a confirmação do servidor.
 */
export async function logout(): Promise<void> {
  try {
    await postNoContent('/auth/logout');
  } finally {
    setToken(null);
  }
}
